import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../sessionSlice";
import {
  listQuestions,
  saveQuestion,
  deleteQuestion,
  deleteAllQuestions,
  importQuestionsCsv,
  reorderQuestions,
} from "../../functions/questionsApi";
import { notifyContentChanged } from "../../components/AdminNav";
import { resolveCrossroadsTheme } from "../../../utils/crossroadsTheme";
import QuizTab from "./QuizTab";

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [questions, setQuestions] = useState([]);
  const [source, setSource] = useState("own");
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const resolvedTheme = useMemo(() => resolveCrossroadsTheme(data), [data]);
  const themeColors = resolvedTheme.colors;
  const hasCorrectAnswers = resolvedTheme.hasCorrectAnswers;

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  const reloadQuestions = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setIsLoadingQuestions(true);
    try {
      const result = await listQuestions({
        themeName: currentTheme,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        token: adminToken,
      });
      setQuestions(result.questions);
      setSource(result.source);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load crossroads.");
    } finally {
      setIsLoadingQuestions(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  useEffect(() => {
    reloadQuestions();
  }, [reloadQuestions]);

  /** Run a mutation, reload the list, and refresh the publish status. */
  const mutate = async (fn, { success, failure, rethrow = false }) => {
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const result = await fn();
      await reloadQuestions();
      notifyContentChanged();
      if (success) setSuccessMessage(typeof success === "function" ? success(result) : success);
    } catch (err) {
      setErrorMessage(err?.message || failure);
      if (rethrow) throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveQuestion = ({ action, editId, question }) =>
    mutate(() => saveQuestion({ themeName: currentTheme, token: adminToken, action, editId, question }), {
      success: action === "edit" ? "Crossroads updated (draft)." : "Crossroads added (draft).",
      failure: "Failed to save crossroads.",
      rethrow: true,
    });

  const handleDeleteQuestion = (id) =>
    mutate(() => deleteQuestion({ themeName: currentTheme, id, token: adminToken }), {
      success: "Crossroads deleted.",
      failure: "Failed to delete crossroads.",
    });

  const handleDeleteAll = () =>
    mutate(() => deleteAllQuestions({ themeName: currentTheme, token: adminToken }), {
      success: "All crossroads deleted.",
      failure: "Failed to delete all crossroads.",
    });

  const handleImportCsv = (rows) =>
    mutate(() => importQuestionsCsv({ themeName: currentTheme, rows, token: adminToken }), {
      success: (result) => `Imported ${result.total} crossroads (draft).`,
      failure: "Failed to import CSV.",
      rethrow: true,
    });

  const handleReorder = (ids) => {
    // Optimistic: show the new order immediately, then persist.
    setQuestions((prev) => ids.map((id) => prev.find((q) => q.id === id)).filter(Boolean));
    return mutate(() => reorderQuestions({ themeName: currentTheme, ids, token: adminToken }), {
      failure: "Failed to save the new order.",
    });
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <header className="themeupdate-header themeupdate-panel-wide" data-tour="tour-gc-header">
        <h1 className="themeupdate-title">Crossroads Content</h1>
        <p className="themeupdate-muted">
          Build the journey: each crossroads has a scenario, two roads and a “Did You Know?” learning moment. Both Self Paced and Live Audience modes use this same content.
        </p>
      </header>

      <QuizTab
        questions={questions}
        source={source}
        isLoading={isLoadingQuestions}
        isSaving={isSaving}
        themeColors={themeColors}
        hasCorrectAnswers={hasCorrectAnswers}
        onSaveQuestion={handleSaveQuestion}
        onDeleteQuestion={handleDeleteQuestion}
        onDeleteAll={handleDeleteAll}
        onImportCsv={handleImportCsv}
        onReorder={handleReorder}
        errorMessage={errorMessage}
        successMessage={successMessage}
        setErrorMessage={setErrorMessage}
        setSuccessMessage={setSuccessMessage}
      />

    </div>
  );
};

export default ThemeUpdate;
